import { InfoCircleOutlined, SafetyOutlined } from "@ant-design/icons";
import { Form } from "antd";
import { ColumnsType } from "antd/es/table";
import { LoginAuthorizedRequestModel } from "api/repositories/userManagement/model/loginAuthorized/LoginAuthorizedRequestModel";
import { UserManagementResponseModel } from "api/repositories/userManagement/model/UserManagementResponse";
import ActionButtonComponent from "components/generalComponents/actionButtonComponent/ActionButtonComponent";
import { AuthenticationContext } from "context/AuthenticationContext";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { colorFormat } from "utils/format/ColorFormat";
import { DataFormat } from "utils/format/DataFormat";
import { NumberFormat } from "utils/format/NumberFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";
import { UserManagementConstants } from "../constants/UserManagementConstants";

const UserManagementViewModel = () => {
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [modal, setModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<
    Array<UserManagementResponseModel>
  >([]);
  const [storeOptions, setStoreOptions] = useState<
    { label: string; value: string }[]
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  const [selectedUser, setSelectedUser] =
    useState<UserManagementResponseModel | null>(null);
  const userRepo = ServiceRepo.getUserManagementRepo();
  const { user } = AuthenticationContext();

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const res = await userRepo.getUserListV2({
        page,
        limit: pageSize,
        role: "user",
        ...DataFormat.removeUndefined(values),
      });
      const items = res?.data?.items || [];
      setTotal(res?.data?.pagination?.total || items.length);
      const rows: Array<UserManagementResponseModel> = items.map((it, idx) => ({
        key: String(idx),
        name: it?.fullName || it?.userName || it?.phone,
        phone: it?.phone || "",
        type: it?.type,
        status: it?.status ?? 1,
        ...it,
      }));
      setDataSource(rows);
    } catch (e) {
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const authorizeLogin = async (body: LoginAuthorizedRequestModel) => {
    try {
      const res = await userRepo?.loginAuthorized(body);
      if (res?.data) {
        getMessage(
          strings.UserManagement.RuleMessage.AuthorizeSuccess,
          3,
          "success"
        );
        window.open(
          `${CacheKeyEnum.authorizedUrl}?token=${res?.data?.token}`,
          "_blank"
        );
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    }
  };

  const handleSearch = async () => {
    setPage(0);
    await fetchStaff();
  };
  const handleReset = async () => {
    form.resetFields();
    setPage(0);
    await fetchStaff();
  };

  const columns: ColumnsType<UserManagementResponseModel> = [
    {
      title: strings.UserManagement.Columns.FullName,
      key: "user-fullname",
      dataIndex: "fullName",
      align: "center",
    },
    {
      title: strings.UserManagement.Columns.Phone,
      key: "user-phone",
      dataIndex: "phone",
      align: "center",
    },
    {
      title: strings.UserManagement.Columns.Email,
      key: "user-email",
      dataIndex: "email",
      align: "center",
    },
    {
      title: strings.GlobalLabels.Balance,
      key: "user-balance",
      dataIndex: "wallet",
      align: "center",
      render: (_, record) => (
        <span
          className={`${
            record?.wallet?.balance > 0 ? "text-green-600" : "text-red-600"
          } font-semibold`}
        >
          {NumberFormat.currencyFormat(
            record?.wallet?.balance,
            record?.wallet?.currency
          )}
        </span>
      ),
    },
    {
      title: strings.GlobalLabels.Type,
      key: "user-type",
      dataIndex: "type",
      align: "center",
      render: (value) =>
        UserManagementConstants.userTypeMapping(value, strings),
    },
    {
      title: strings.UserManagement.Columns.Role,
      key: "user-role",
      dataIndex: "role",
      align: "center",
      render: (_, record) => record?.role?.name,
    },
    {
      title: strings.GlobalLabels.Status,
      key: "user-status",
      dataIndex: "status",
      align: "center",
      render: (status) => {
        const isActive = status === 0;
        return (
          <div className="flex items-center justify-center gap-2">
            <span className={isActive ? "text-green-600" : "text-red-500"}>
              {isActive
                ? strings.GlobalLabels.Active
                : strings.GlobalLabels.Inactive}
            </span>
          </div>
        );
      },
    },
    {
      title: strings.GlobalLabels.createdAt,
      key: "user-created",
      dataIndex: "createdAt",
      align: "center",
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      title: strings.GlobalLabels.Detail,
      key: "detail",
      align: "center",
      width: "16%",
      fixed: "right",
      render: (_, record) => {
        const isAbleToAuthorized =
          record?.role?.displayCode === "user" &&
          record?.status === 0 &&
          ["moderator", "admin", "super_admin"].includes(user?.role ?? "");

        return (
          <ActionButtonComponent
            data={{
              buttonProps: [
                {
                  className: "font-semibold",
                  type: "link" as const,
                  icon: <InfoCircleOutlined />,
                  label: strings.GlobalLabels.Detail,
                  onClick: async () => {
                    setSelectedUser(record);
                    setModal(true);
                  },
                },
                isAbleToAuthorized
                  ? {
                      className: "font-semibold",
                      type: "link" as const,
                      icon: <SafetyOutlined />,
                      colorPrimary: colorFormat?.Purple,
                      label: strings.UserManagement.Labels.Authorize,
                      onClick: async () =>
                        await authorizeLogin({ phone: record?.phone }),
                    }
                  : undefined,
              ].filter(Boolean),
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    fetchStaff();
  }, [page, pageSize]);

  return {
    form,
    page,
    pageSize,
    total,
    loading,
    dataSource,
    columns,
    modal,
    storeOptions,
    selectedUser,
    updateForm,
    setSelectedUser,
    setStoreOptions,
    setModal,
    setPage,
    setPageSize,
    handleReset,
    handleSearch,
  };
};

export default UserManagementViewModel;
